import { format }   from 'libphonenumber-js';
import badwords from 'badwords-list';

/**
 * @method camelize
 * @description This takes 'A phrase like this' and outputs 'aPhraseLikeThis'.
 * @param input {string}
 * @return {string}
 */
export function camelize(input: string) {
    return input.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
        if (+match === 0) return ''; // kill that whitespace
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    });
}

/**
 * @method capitalizeFirstLetter
 * @description Transform a string so that each word has its first letter capitalized, followed by lowercase letters
 * @param input {string}
 * @return {string}
 */
export function capitalizeFirstLetter(input: string) {
    if (!input) {
        return null;
    }
    const capitalizedWords = [];
    for (const item of input.split(' ')) {
        capitalizedWords.push(item.charAt(0).toUpperCase() + item.substring(1).toLowerCase());
    }
    return capitalizedWords.join(' ');
}

/**
 * @method formatRecipientName
 * @description Return either the formatted recipient name or 'Contact' for contacts with no name
 * @param input {string}
 * @return {string}
 */
export function formatRecipientName(name: string) {
    if (name && name.trim()) {
        return capitalizeFirstLetter(name.trim());
    }
    return 'Contact';
}

/**
 * @method obfuscatePhoneNumber
 * @description Take a phone number, format it as an international number, and use the obfuscate function below to hide certain digits
 * @param input {string}
 * @return {string}
 */
export function obfuscatePhoneNumber(input: string) {
    if (!input) {
        return null;
    }
    const formattedString = format(input, 'US', 'International');

    return formattedString.replace(/(\+\d)\s(\d\d\d)(\s\d\d\d\s\d)(\d\d\d)/, obfuscate);
}

/**
 * @method obfuscate
 * @description regex replace function, replacing four digits of a phone number with * characters
 * @param match
 * @param p1
 * @param p2
 * @param p3
 * @param p4
 * @return {string}
 */
export function obfuscate(match, p1, p2, p3, p4) {
    if (typeof match === 'undefined') {
        return null;
    }
    return p1 + ' (' + p2 + ') *** - *' + p4;
}

/**
 * @method formatAccountUrl
 * @description Create a Statflo CRM account URL by base64 encoding the ban number and joining it to the webapp host accounts url
 * @param ban_id {string}
 * @param context {*}
 * @return {string}
 */
export function formatAccountUrl(banId?: string, isOnCallList?: boolean, callListId?: number) {
    if (!banId) {
        return null;
    }

    const callListAddon = isOnCallList ? '&list=call' : '';
    const callListIdAddOn = callListId ? `&calllist_id=${callListId}` : '';
    const encodedAccount = window.btoa(banId.toString());
    const accountURL = `${window.location.protocol}//${window.location.hostname}/accounts?client=${encodedAccount}${callListIdAddOn}${callListAddon}`;

    return accountURL;
}


/**
 * @method replaceWithAsterisks
 * @description Returns a string of censoring characters (default: *) the same length as the input string.
 * @param word {string}
 * @return {string}
 */
export function replaceWithAsterisks(word: string, maskCharacter: string = '*') {
    let i = 0;
    let censoredWord = '';

    while (i < word.length) {
        censoredWord += maskCharacter;
        i++;
    }

    return censoredWord;
}

const checkWord = (word: string) =>
    badwords.object.hasOwnProperty(word.toLowerCase()) ? // If the word is in the object of bad words
        replaceWithAsterisks(word) :                     // censor the word
        word;                                            // otherwise return the word as-is

/**
 * @method censorProfanity
 * @description Returns a string with any profanity censored with asterisks
 * @param sentence {string}
 * @return {string}
 */
export const censorProfanity = (sentence: string) =>
    sentence.split(/\b/) // Split on word boundaries
        .map(checkWord)  // process each word to check if it's profane and censor appropriately
        .join('');       // Connect the string again


export const textToTranslationKey = (text: string): string => text.replace(/\s+/g, '_').replace(/[^\w\-]+/g, '').toLowerCase();



// WEBPACK FOOTER //
// ./src/helpers/stringUtils.ts